import { addYears, getMonth, setMonth, startOfMonth, subYears } from "date-fns";
import { forwardRef, useMemo, useCallback } from "react";
import ArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import ArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useTranslation } from "react-i18next";
import CONSTANTS from "core/constants";
import {
  Container,
  InnerContainer,
  YearPickerDiv,
  MonthPickerText,
  MonthPickerDiv,
  MonthPickerElement,
  MonthPickerElementText,
  Icon,
} from "./monthCalendar.styles";
import { useWeekDate } from "components/booking/hooks/useWeekDate";

type Props = {
  isOpen: boolean;
  onSelect: () => void;
};

const MonthCalendar = forwardRef<HTMLDivElement, Props>(
  ({ isOpen, onSelect }) => {
    const { t } = useTranslation();
    const { date: currentDate, setWeekDate } = useWeekDate();

    const navigateToPreviousYear = useCallback(() => {
      setWeekDate(subYears(currentDate, 1));
    }, [currentDate, setWeekDate]);

    const navigateToNextYear = useCallback(() => {
      setWeekDate(addYears(currentDate, 1));
    }, [currentDate, setWeekDate]);

    const selectMonth = useCallback(
      (monthToConvert: number) => {
        const getSelectedMonth = setMonth(
          startOfMonth(currentDate),
          monthToConvert,
        );
        onSelect();
        setWeekDate(getSelectedMonth);
      },
      [currentDate, onSelect, setWeekDate],
    );

    const getMonths = useMemo(
      () =>
        CONSTANTS.CALENDAR.SHORT_MONTH.map((m) => {
          return (
            <MonthPickerElement
              data-testid="month-clicker"
              onClick={() => selectMonth(m.key)}
              key={m.key}
            >
              <MonthPickerElementText
                isCurrentMonth={m.key === getMonth(currentDate)}
                data-testid="month-of-year"
              >
                {t(m.label)}
              </MonthPickerElementText>
            </MonthPickerElement>
          );
        }),
      [currentDate, selectMonth, t],
    );

    if (!isOpen) return null;

    return (
      <div data-testid="month-calendar-div">
        <Container>
          <InnerContainer>
            <YearPickerDiv>
              <Icon
                data-testid="navigate-prev-year"
                onClick={navigateToPreviousYear}
              >
                <ArrowLeft />
              </Icon>
              <MonthPickerText data-testid="displayed-year">
                {currentDate.getFullYear()}
              </MonthPickerText>
              <Icon
                data-testid="navigate-next-year"
                onClick={navigateToNextYear}
              >
                <ArrowRight />
              </Icon>
            </YearPickerDiv>
            <MonthPickerDiv>{getMonths}</MonthPickerDiv>
          </InnerContainer>
        </Container>
      </div>
    );
  },
);

MonthCalendar.displayName = "MonthCalendar";

export default MonthCalendar;
